import { Field, FieldProps } from 'formik';
import React, { ReactNode } from 'react';
import { InjectedIntl, injectIntl } from 'react-intl';
import styled from 'styled-components';
import InputField from '../../components/InputField';
import ValidationErrors from '../validationErrors';
import ValueFormatters from '../valueFormatters';
import { TranslationHelpers } from '../../../../i18n/translationHelpers';
import { PostalCodeValidation } from '../postalcode/PostalCodeValidation';
import { COLORS, FONT_FAMILY } from '../../constants/styles';
import { IValidatorAddress, BanchiValidator } from './BanchiValidation';
import { ValidatorRule } from '../../validator/validatorRule.constants';
import { AddressLineFieldValidator } from '../../validator/addressLineFieldValidator';
import { IErrorMessage } from '../../components/errorMessage/errorMessage.type';

const NO_BANCHI = '無番地';

interface IBanchiAddressLineFieldProps {
    autoFocus?: boolean;
    intl: InjectedIntl;
    value?: string;
    required?: boolean;
    masterAddress?: IValidatorAddress | null;
    validatorRules?: ValidatorRule[];
}

const NoBanchiBoxWrapper = styled.div`
    margin-top: 6px;
    color: ${COLORS.grey3};
    font-size: 0.8125rem;
    margin-left: 0.625rem;
`;

const NoBanchiLink = styled.a`
    color: ${COLORS.linkBlue};
    cursor: pointer;
    transition: font-family 200ms ease-in;
    text-decoration: none;
    &:hover {
        ${FONT_FAMILY.multipleBoldCss};
        font-weight: 700;
    }
`;

class BanchiAddressLineField extends React.Component<IBanchiAddressLineFieldProps> {
    private readonly ID: string;
    private readonly LABEL_ID: string;
    private readonly ERROR_REQUIRED_ID: string;
    private readonly MAX_LENGTH: number;
    private readonly BANCHI_LABEL_ID: string;
    private userAddress: IValidatorAddress;

    private isJapan = true;

    private addressLineFieldValidator: AddressLineFieldValidator;

    constructor(props: IBanchiAddressLineFieldProps) {
        super(props);

        this.ID = `addressLine1`;
        this.LABEL_ID = 'label.address_line_jp';
        this.ERROR_REQUIRED_ID = 'error.address_line_required';
        this.MAX_LENGTH = 85;

        this.BANCHI_LABEL_ID = 'label.banchi';

        this.validate = this.validate.bind(this);
        this.renderInputField = this.renderInputField.bind(this);
        const { validatorRules = [] } = props;
        this.addressLineFieldValidator = new AddressLineFieldValidator(validatorRules);
        this.userAddress = { state: '', city: '', street: '' };
    }

    public render() {
        return <Field name={this.ID} validate={this.validate} render={this.renderInputField} />;
    }

    private validate(value: string): IErrorMessage {
        const { intl, required, masterAddress, validatorRules = [] } = this.props;

        if (!value && required) {
            return intl.formatMessage(
                TranslationHelpers.getMessageDescriptor(this.ERROR_REQUIRED_ID),
            );
        }

        if (value.length > this.MAX_LENGTH) {
            return ValidationErrors.fieldTooLongMessage(intl, this.MAX_LENGTH);
        }

        if (value && validatorRules.length > 0) {
            const err = this.addressLineFieldValidator.validate(value, this.LABEL_ID);
            if (err !== null) {
                return err.message;
            }
        }

        if (this.isJapan && !this.isNoBanchi()) {
            if (masterAddress) {
                if (!BanchiValidator.haveBanchiValidator(this.userAddress, masterAddress)) {
                    return ValidationErrors.banchiRequiredMessage(intl);
                }

                return '';
            } else {
                if (
                    !BanchiValidator.containsNumber(value) &&
                    !BanchiValidator.containsNumber(this.userAddress.city)
                ) {
                    return ValidationErrors.banchiRequiredMessage(intl);
                }
                return '';
            }
        }

        return '';
    }

    private isNoBanchi() {
        return (this.props.value ?? '').indexOf(NO_BANCHI) > -1;
    }

    private renderInputField({
        form: { touched, errors, setFieldValue, handleBlur, values },
    }: FieldProps): ReactNode {
        const { autoFocus, intl, value, required, masterAddress } = this.props;
        this.isJapan = !PostalCodeValidation.isOverSeasPostalCode(values.postalCode);
        const isNoBanchi = this.isNoBanchi();
        const isTouched = touched && touched[this.ID];
        const errorMessage = isTouched && errors && errors[this.ID] ? errors[this.ID] : undefined;
        const label = intl.formatMessage(TranslationHelpers.getMessageDescriptor(this.LABEL_ID), {
            n: 1,
        });
        const banchiLabel = intl.formatMessage(
            TranslationHelpers.getMessageDescriptor(this.BANCHI_LABEL_ID),
        );

        const onBlur = (event: React.FocusEvent<any>) => {
            setFieldValue(this.ID, ValueFormatters.trimExtraSpaces(event.target.value));
            handleBlur(event);
        };

        const onClickNoBanchi = (e: React.MouseEvent) => {
            e.preventDefault();
            setFieldValue(this.ID, (value ?? '') + NO_BANCHI);
        };

        this.userAddress.state = values['state'];
        this.userAddress.city = values['city'];
        this.userAddress.street = values['addressLine1'];

        return (
            <InputField
                autoComplete="address-line1"
                labelMessage={label}
                type="text"
                id={this.ID}
                maxLength={this.MAX_LENGTH}
                name={this.ID}
                value={value}
                error={errorMessage}
                autoFocus={autoFocus}
                required={required}
                onBlur={onBlur}
                className={'notranslate'}
            >
                {this.isJapan && (
                    <NoBanchiBoxWrapper>
                        {isNoBanchi ||
                        (masterAddress &&
                            BanchiValidator.haveBanchiValidator(
                                this.userAddress,
                                masterAddress,
                            )) ? (
                            banchiLabel
                        ) : (
                            <NoBanchiLink tabIndex={0} onClick={onClickNoBanchi}>
                                {banchiLabel}
                            </NoBanchiLink>
                        )}
                    </NoBanchiBoxWrapper>
                )}
            </InputField>
        );
    }
}

export default injectIntl(BanchiAddressLineField);
